import { BreakPoint } from '@adornis/base/client/frontend-helpers/window-size.js';
import { logger } from '@adornis/base/logging.js';
import type { PartialEntityData } from '@adornis/baseql/entities/types.js';
import type { BaseQLFieldDefinition } from '@adornis/baseql/metadata/types.js';
import { BHDialogFreeFormMails } from '@adornis/bh-mails/client/bh-dialog-free-form-mails.js';
import { matchingConfig } from '@adornis/bh-matching-config/config.js';
import { configRegistry } from '@adornis/bh-matching-config/entities/global-config.js';
import { sendFreeFormMailToSingleUser } from '@adornis/bh-matching-mails/api/user-mail.js';
import { BuddyMailTemplate, MailTemplateType } from '@adornis/bh-matching-mails/entities/mail-templates.js';
import type { TranslationDictionaryBuddy } from '@adornis/bh-matching-translation/translations.js';
import { MatchingUserAPI } from '@adornis/bh-matching-user/api/MatchingUserAPI.js';
import { MatchingFormDialog } from '@adornis/bh-matching-user/components/matching-form-dialog.js';
import type { BuddyUser } from '@adornis/bh-matching-user/entities/buddy-user.js';
import { getMenteeUserClass } from '@adornis/bh-matching-user/user-class.js';
import { userFieldBlackList } from '@adornis/bh-matching-user/user-field-blacklist.js';
import type { User } from '@adornis/buchungsheld-db/entities/user.js';
import { TrDialog } from '@adornis/buchungsheld-theme/components/tr-dialog.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { ComponentSizeController } from '@adornis/chemistry/controllers/component-size-controller.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-button.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import '@adornis/chemistry/elements/components/x-grid.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import { AdornisFile } from '@adornis/file-utils/db/files.js';
import { Bold } from '@adornis/tiptap/client/extensions/Bold.js';
import { Document } from '@adornis/tiptap/client/extensions/Document.js';
import { Dropcursor } from '@adornis/tiptap/client/extensions/Dropcursor.js';
import { HardBreak } from '@adornis/tiptap/client/extensions/HardBreak.js';
import { Italic } from '@adornis/tiptap/client/extensions/Italic.js';
import { Link } from '@adornis/tiptap/client/extensions/Link/Link.js';
import { Paragraph } from '@adornis/tiptap/client/extensions/Paragraph.js';
import { Strike } from '@adornis/tiptap/client/extensions/Strike.js';
import { Text } from '@adornis/tiptap/client/extensions/Text.js';
import { TextStyle } from '@adornis/tiptap/client/extensions/TextStyle.js';
import { TransformerHTML } from '@adornis/tiptap/client/extensions/TransformerHTML.js';
import { Underline } from '@adornis/tiptap/client/extensions/Underline.js';
import type { XTiptap } from '@adornis/tiptap/client/x-tiptap.js';
import { TranslationController } from '@adornis/translation-core/client/translation-controller.js';
import { format } from 'date-fns';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { createRef, ref } from 'lit/directives/ref.js';
import { repeat } from 'lit/directives/repeat.js';

/*
 * @element buddy-user-details
 */
@customElement('buddy-user-details')
export class BuddyUserDetails extends ChemistryLitElement {
  @property({ attribute: false }) user = new RXController<BuddyUser | null>(this, null);
  @property({ attribute: 'always-open', type: Boolean }) alwaysOpen?: boolean;
  @property({ attribute: 'hide-toggle', type: Boolean }) hideToggle?: boolean;

  @property({ attribute: false }) fieldBlackList?: string[];
  @property({ attribute: false }) overrideActions?: Record<string, () => unknown>;

  @state() protected readonly _translator = new TranslationController<TranslationDictionaryBuddy>(this);
  @state() protected readonly _size = new ComponentSizeController(this);
  @state() protected _isOpen = false;

  @state() private _translation = new TranslationController<TranslationDictionaryBuddy>(this);

  actions = {
    edit: () => html`
      <tr-button-small
        mode="icon"
        icon="edit"
        ${css({ width: 'min-content' })}
        tone=${this.colors.tone.onyx}
        @click=${async event => {
          event.stopPropagation();
          const target = this.user.value;
          if (!target) return;

          MatchingFormDialog.showDialog(target as any);
        }}
      ></tr-button-small>
    `,

    sendMailToUser: () => html`
      <tr-button-small
        mode="icon"
        icon="mail"
        ${css({ width: 'min-content' })}
        tone=${this.colors.tone.onyx}
        @click=${async event => {
          event.stopPropagation();
          this._sendFreeFormMail(this.user.value as any);
        }}
      ></tr-button-small>
    `,
  };

  override render() {
    return html`
      <x-flex space="md" h100 flex @click=${() => (this._isOpen = true)} ${css({ position: 'relative' })}>
        <x-flex space="xs" horizontal>
          ${Object.values({ ...this.actions, ...this.overrideActions }).map(action => action())}
        </x-flex>

        ${
          this._isOpen || this.alwaysOpen
            ? html`
                ${this._userDetails()}
                <x-hr></x-hr>
                ${this._note()}
              `
            : html` ${this._userDetails(true)} `
        }
        ${
          this.alwaysOpen || this.hideToggle
            ? nothing
            : html`
                ${
                  !!this.user.value?.note?.replace(/(<([^>]+)>)/gi, '')
                    ? html`
                        <x-icon
                          ${css({
                            fontSize: '18px',
                            position: 'absolute',
                            top: '3px',
                            right: '25px',
                            color: this.colors.tone.warning,
                            opacity: '.6',
                          })}
                          tone
                        >
                          note
                          <x-tooltip
                            .text=${html` <x-text> ${this._translation.translate('user', 'note_exists')} </x-text> `}
                          ></x-tooltip>
                        </x-icon>
                      `
                    : nothing
                }

                <x-button
                  mode="icon"
                  icon=${this._isOpen ? 'expand_more' : 'chevron_right'}
                  ${css({ position: 'absolute', top: '0', right: '0' })}
                  @click=${(e: MouseEvent) => {
                    e.stopPropagation();
                    this._isOpen = !this._isOpen;
                  }}
                >
                </x-button>
              `
        }
      </x-flex>
    `;
  }

  protected _userDetails(preview?: boolean) {
    const user = this.user.value;
    if (!user) return html` <tr-text> 404 - No user found! </tr-text> `;

    //const render = rena
    const allFields = Object.values((this.user.value?.constructor as typeof BuddyUser).fields).filter(
      field => !(this.fieldBlackList ?? userFieldBlackList).includes(field.name),
    );
    const previewFields = ['firstName', 'lastName', 'gender', 'mainSubject', 'createdAt'];
    let fields = allFields;

    if (preview) {
      fields = allFields
        .filter(curField => previewFields.includes(curField.name))
        .sort((fieldA, fieldB) => previewFields.indexOf(fieldA.name) - previewFields.indexOf(fieldB.name));
    } else if (configRegistry.fieldsOverride) {
      fields = configRegistry
        .fieldsOverride(this.user.value?.constructor as any)
        .map(({ name }) => {
          return allFields.find(
            field =>
              field.name === name ||
              field.name ===
                'germanProficiencyOld' /* exception for field that should only exist in the admin area, add a config option if that happens again in the future */,
          );
        })
        .filter(Boolean) as any;
    }

    return html`
      <x-flex flex space="sm">
        <x-grid space="xs" flex columns=${(this._size.componentDevice as number) >= BreakPoint.phone ? '1fr 2fr' : '1'}>
          ${repeat(fields, field => this._userDetailsField(field, user[field.name]))}
        </x-grid>
      </x-flex>
    `;
  }

  protected _userDetailsField(field: BaseQLFieldDefinition, value: any) {
    const getFieldTranslation = (field: BaseQLFieldDefinition, value: any) => {
      if (!value) return '-';
      if (Array.isArray(value) && value.length === 0) return '-';

      const fieldType = field.type();
      if ((fieldType as any)._isBaseQLEnum) {
        if (Array.isArray(value)) return value.map(v => getFieldTranslation(field, v)).join(', ');
        const translation = this._translator.translate('enums', value);
        return !translation.startsWith('enums.') ? translation : value;
      }
      if (value instanceof Date) return format(value, 'dd.MM.yyyy');
      if (value instanceof AdornisFile)
        return html`<a href=${value.getServeLink() + `/einverständiserklärung.${value.meta?.extension}`}>
          ${value.meta?.fileName}.${value.meta?.extension}
        </a>`;

      if (typeof value === 'boolean')
        return value ? this._translator.translate('yes') : this._translator.translate('no');

      return value;
    };
    return html`
      <tr-text bold> ${this._translator.translate('field', field.name as any)} </tr-text>
      <tr-text ${css({ overflowWrap: 'break-word', wordBreak: 'break-word' })}>
        ${getFieldTranslation(field, value)}
      </tr-text>
    `;
  }

  protected _noteEl = createRef<XTiptap>();
  protected _note() {
    const user = this.user.value;
    if (!user) return nothing;

    const extensions = [
      [TransformerHTML, Document, Text, TextStyle, HardBreak, Dropcursor, Bold, Italic, Underline, Strike, Paragraph()],
      [Link],
    ];
    return html`
      <x-flex space="xs">
        <x-text bold> ${this._translator.translate('buddy', 'note')} </x-text>
        <x-tiptap
          placeholder=${this._translator.translate('buddy', 'note')}
          .extensions=${extensions}
          .value=${(user as any).note}
          ${ref(this._noteEl)}
        ></x-tiptap>
        <x-flex horizontal end>
          <x-button mode="text" icon="save" @click=${() => this._saveNote()}>
            ${this._translator.translate('buddy', 'saveNote')}
          </x-button>
        </x-flex>
      </x-flex>
    `;
  }

  private async _saveNote() {
    const note = this._noteEl.value?.value.value as string;
    if (note && this.user.value && note !== (this.user.value as any).note) {
      try {
        await TrDialog.waitFor(MatchingUserAPI.saveNote((this.user.value as any)._id, note)());
        void XSnackbar.show('Notizen wurden erfolgreich gespeichert.');
      } catch (err) {
        logger.error({ err });
        void TrDialog.alert('Es gab ein Problem beim Speichern der Notiz. Bitte versuchen Sie es später erneut.');
      }
    }
  }

  protected _sendFreeFormMail(user?: User) {
    if (!user) throw new Error('user is required');
    const mailAddresses = [user.email];
    const isMentee = user instanceof getMenteeUserClass();

    BHDialogFreeFormMails.showPopup({
      props: {
        mailAddresses,
        mailTemplateType: isMentee ? MailTemplateType.FREE_FORM_MENTEE : MailTemplateType.FREE_FORM_MENTOR,
        allowSelectingTemplate: false,
        copyMailAddress: matchingConfig.get('DIRECT_MAIL_COPY_ADDRESS'),
        mailTemplateConstructor: (doc: PartialEntityData<BuddyMailTemplate>) => new BuddyMailTemplate(doc),
        mailSendOperation: (matchIDs: string[], mailTemplate: BuddyMailTemplate) => {
          return sendFreeFormMailToSingleUser(user._id, mailTemplate)();
        },
      },
    });
  }
}
